import { BorderColorOutlined, DescriptionOutlined, Search as SearchIcon } from '@mui/icons-material'
import { Card, CardHeader, CardContent, Box, InputAdornment, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { searchFieldSx } from '../../../common/searchFieldSx'
import { colors, spacingItem } from '../../../theme'
import { DocumentItem } from './DocumentItem'
import { AddDocumentModal } from './AddDocumentModal'
import { AnimalDocumentDto, AnimalDto } from '../../../../interactors/gen/backendClient'
import { getReadableAnimalDocumentType } from '../../../../domain/Animal/AnimalDisplay'
import { useFetchOrRefreshMembersOnMount } from '../../../../store/useFetchOrRefreshMembersOnMount'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import { useFetchOrRefreshAdoptersOnMount } from '../../../../store/useFetchOrRefreshAdoptersOnMount'
import { useAdoptersStore } from '../../../../store/AdoptersStore'
import { AdoptionDocumentItem } from './AdoptionDocumentItem'
import { fullName } from '../../../../domain/Adopters/AccountDisplay'
import { OfferUsageLimitBanner } from '../../../common/OfferUsageLimitBanner'
import { getRelevantAdoptionContextForAnimal } from '../../../../utils/getRelevantAttemptForAnimal'
import { getAnimalDocumentsQuota } from '../../../../domain/Customer/ContentLimits'
import { useLimitedContentAccess } from '../../../../utils/hooks/useLimitedContentAccess'

interface DocumentsListProps {
  documents: AnimalDocumentDto[]
  animal: AnimalDto
}

export const DocumentsList: React.FC<DocumentsListProps> = ({ documents, animal }) => {
  useFetchOrRefreshMembersOnMount()
  useFetchOrRefreshAdoptersOnMount()

  const adoptersStore = useAdoptersStore()
  const relevantAdoptionContext = getRelevantAdoptionContextForAnimal(
    adoptersStore.adopters,
    animal.id,
    animal.adoption?.status,
    animal.adoption?.adopterId
  )
  const adopter = relevantAdoptionContext?.adopter
  const adoptionAttempt = relevantAdoptionContext?.attempt

  const canEditAnimal = useCanEditAnimal()

  const [searchQuery, setSearchQuery] = useState('')
  const [documentsList, setDocumentsList] = useState<AnimalDocumentDto[]>(documents || [])
  const safeDocumentsList = (documentsList || []).filter((document): document is AnimalDocumentDto =>
    Boolean(document?.id)
  )
  const limitedContentAccess = useLimitedContentAccess()
  // Documents the adoption workflow attaches by itself are not counted, as on the backend.
  const documentsQuota = getAnimalDocumentsQuota(limitedContentAccess, safeDocumentsList)
  const hasReachedDocumentsLimit = documentsQuota != null && documentsQuota.remaining === 0

  useEffect(() => {
    setDocumentsList(documents || [])
  }, [documents])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const filteredDocuments = safeDocumentsList.filter(
    (document) =>
      searchQuery === '' ||
      document.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      document.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getReadableAnimalDocumentType(document.type)?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" sx={{ ...spacingItem, backgroundColor: colors.white }}>
        <TextField
          id="name-input"
          placeholder="Rechercher un document"
          type="text"
          fullWidth
          value={searchQuery}
          onChange={handleSearch}
          sx={searchFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <DescriptionOutlined sx={{ color: '#698DAC', fontSize: 23 }} />
              <Box component="span">Documents</Box>
            </Box>
          }
        />
        <CardContent>
          {documentsQuota != null && (
            <OfferUsageLimitBanner
              title={
                documentsQuota.used > documentsQuota.max
                  ? `${documentsQuota.used} documents enregistrés`
                  : `${documentsQuota.used} document${documentsQuota.used > 1 ? 's' : ''} sur ${
                      documentsQuota.max
                    } utilisé${documentsQuota.used > 1 ? 's' : ''}`
              }
              description={
                documentsQuota.used > documentsQuota.max
                  ? `La fiche de ${animal.name} dépasse actuellement la limite de ${documentsQuota.max} document inclus dans l'offre gratuite. Les documents existants restent accessibles, mais vous devez passer à l'offre Essentielle ou configurer Stripe Connect pour en ajouter un nouveau.`
                  : hasReachedDocumentsLimit
                  ? `La fiche de ${animal.name} a atteint la limite incluse dans l'offre gratuite. Passez à l'offre Essentielle ou configurez Stripe Connect pour ajouter un nombre illimité de documents à vos fiches animales.`
                  : `Il reste ${documentsQuota.remaining} document${
                      documentsQuota.remaining > 1 ? 's' : ''
                    } à ajouter sur la fiche de ${
                      animal.name
                    } avec l'offre gratuite. Passez à l'offre Essentielle ou configurez Stripe Connect pour un nombre illimité de documents.`
              }
              currentCount={documentsQuota.used}
              limit={documentsQuota.max}
              ctaLabel="Découvrir l'offre Essentielle"
            />
          )}
          {filteredDocuments.length > 0 &&
            filteredDocuments.map((document) => (
              <DocumentItem
                key={document.id}
                document={document}
                animalId={animal.id}
                setDocumentsList={setDocumentsList}
              />
            ))}
          <Box display="flex" justifyContent="flex-end" sx={spacingItem}>
            <AddDocumentModal
              animalId={animal.id}
              disabled={!canEditAnimal || hasReachedDocumentsLimit}
              disabledReason={`Vous avez atteint la limite de ${documentsQuota?.max} document incluse dans l'offre gratuite.`}
            />
          </Box>
        </CardContent>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <BorderColorOutlined sx={{ color: '#D39A12', fontSize: 23 }} />
              <Box component="span">Adoption</Box>
            </Box>
          }
        />
        <CardContent>
          {adoptionAttempt &&
            adoptionAttempt.certificate &&
            adoptionAttempt.certificate.signed &&
            adoptionAttempt.certificate.signedDate && (
              <AdoptionDocumentItem
                type="certificate"
                documentKey={adoptionAttempt.certificate.signed}
                uploadedAt={adoptionAttempt.certificate.signedDate}
                fullName={adopter ? fullName(adopter) : ''}
              />
            )}
          {adoptionAttempt &&
            adoptionAttempt.contract &&
            adoptionAttempt.contract.signed &&
            adoptionAttempt.contract.signedDate && (
              <AdoptionDocumentItem
                type="contract"
                documentKey={adoptionAttempt.contract.signed}
                uploadedAt={adoptionAttempt.contract.signedDate}
                fullName={adopter ? fullName(adopter) : ''}
              />
            )}
          {adoptionAttempt &&
            adoptionAttempt.sterilization &&
            adoptionAttempt.sterilization.file &&
            adoptionAttempt.sterilization.documentSentDate && (
              <AdoptionDocumentItem
                type="sterilization"
                documentKey={adoptionAttempt.sterilization.file}
                uploadedAt={adoptionAttempt.sterilization.documentSentDate}
                fullName={adopter ? fullName(adopter) : ''}
              />
            )}
          {adoptionAttempt &&
            adoptionAttempt.documents &&
            adoptionAttempt.documents.length > 0 &&
            adoptionAttempt.documents.map((document) => (
              <AdoptionDocumentItem
                key={document.id}
                type={document.type}
                documentKey={document.documentKey}
                uploadedAt={document.uploadedAt}
                fullName={adopter ? fullName(adopter) : ''}
              />
            ))}
        </CardContent>
      </Card>
    </>
  )
}
